<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';

const props = defineProps({
  isEmailOrWebWidgetInbox: { type: Boolean, required: true },
  hasErrors: { type: Boolean, default: false },
  hasAttachments: { type: Boolean, default: false },
  allowSignature: { type: Boolean, default: false },
  allowToggleSignature: { type: Boolean, default: true },
  messageSignature: { type: String, default: '' },
  channelType: { type: String, default: '' },
  targetSchema: { type: String, default: 'markdown' },
  inboxId: { type: Number, default: null },
  showImageResizeToolbar: { type: Boolean, default: false },
  variables: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['cannedResponseSelected']);

const { t } = useI18n();

const modelValue = defineModel({
  type: String,
  default: '',
});

const state = ref({
  hasSlashCommand: false,
  showMentions: false,
  hasMentionResults: false,
  mentionSearchKey: '',
});

watch(
  modelValue,
  newValue => {
    if (props.isEmailOrWebWidgetInbox) return;

    const bodyWithoutSignature = newValue;

    // Check if message starts with slash
    const startsWithSlash = bodyWithoutSignature.startsWith('/');

    // Update slash command and mentions state
    state.value = {
      ...state.value,
      hasSlashCommand: startsWithSlash,
      showMentions: startsWithSlash,
      mentionSearchKey: startsWithSlash ? bodyWithoutSignature.slice(1) : '',
    };
  },
  { immediate: true }
);

const hideMention = () => {
  state.value.showMentions = false;
};

const onMentionResults = hasResults => {
  state.value.hasMentionResults = hasResults;
};

const replaceText = async (message, meta = {}) => {
  // Only append signature on replace if allowSignature is true
  const finalMessage = message;

  await nextTick();
  modelValue.value = finalMessage;
  emit('cannedResponseSelected', meta);
};
</script>

<template>
  <div class="flex-1 h-full" :class="[!hasAttachments && 'min-h-[200px]']">
    <template v-if="isEmailOrWebWidgetInbox">
      <Editor
        v-model="modelValue"
        :placeholder="
          t('COMPOSE_NEW_CONVERSATION.FORM.MESSAGE_EDITOR.PLACEHOLDER')
        "
        class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4 [&>div]:!bg-transparent h-full"
        :class="
          hasErrors
            ? '[&_.empty-node]:before:!text-n-ruby-9 [&_.empty-node]:dark:before:!text-n-ruby-9'
            : ''
        "
        enable-variables
        :variables="props.variables"
        :show-character-count="false"
        :channel-type="props.channelType"
        :target-schema="props.targetSchema"
        :signature="props.messageSignature"
        :allow-signature="props.allowSignature"
        :inbox-id="props.inboxId"
        :show-image-resize-toolbar="props.showImageResizeToolbar"
        @canned-response-selected="meta => emit('cannedResponseSelected', meta)"
      />
    </template>
    <template v-else>
      <TextArea
        v-model="modelValue"
        :placeholder="
          t('COMPOSE_NEW_CONVERSATION.FORM.MESSAGE_EDITOR.PLACEHOLDER')
        "
        class="!px-0 [&>div]:!px-4 [&>div]:!border-transparent [&>div]:!bg-transparent"
        :custom-text-area-class="
          hasErrors
            ? 'placeholder:!text-n-ruby-9 dark:placeholder:!text-n-ruby-9'
            : ''
        "
        auto-height
        :signature="messageSignature"
        :allow-signature="props.allowSignature"
        :allow-toggle-signature="props.allowToggleSignature"
      >
        <CannedResponse
          v-if="state.showMentions && state.hasSlashCommand"
          v-on-clickaway="hideMention"
          class="normal-editor__canned-box"
          :search-key="state.mentionSearchKey"
          :inbox-id="props.inboxId"
          @replace="replaceText"
          @has-results="onMentionResults"
        />
      </TextArea>
    </template>
  </div>
</template>
